import React, { useState } from 'react';
import { Button, Dialog, Message, Spin } from '@fx-ui/fine-design';
import { MonoBaseOpenPlatformAdminIcon } from '@fx-ui/icon';
import { DataType, MemberItem } from '@fx-ui/jdy-design-biz';
import { ECode, get, isEqual } from '@libs/shared';
import { getContactDeptList, getContactMemberList } from '../../apis';
import ContactValuePanel from '../../components/contact-value-panel';
import MemberSelectDialog from '../../components/member-select-dialog';
import { API, platformIntl, TRACKER } from '../../constants';
import { authModel } from '../../models';
import { getRequestUrl, post, tracker } from '../../utils';

interface OpenPlatformManagerResponse {
    members: MemberItem[];
}

const getMemberIds = (members: MemberItem[]) => members.map((member) => member._id).sort();

/** 开放平台管理员配置入口 */
const OpenPlatformManager: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [memberSelectVisible, setMemberSelectVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [savedMembers, setSavedMembers] = useState<MemberItem[]>([]);
    const [members, setMembers] = useState<MemberItem[]>([]);
    const [illegalMemberIds, setIllegalMemberIds] = useState<string[]>([]);

    if (!authModel.hasSysAdmin || !authModel.isTenantModuleEnabled('open_platform_manager')) {
        return null;
    }

    const closeDialog = () => {
        setVisible(false);
        setMemberSelectVisible(false);
        setMembers(savedMembers);
        setIllegalMemberIds([]);
    };

    const openDialog = () => {
        tracker(TRACKER.OPEN_PLATFORM_ADMIN);
        setVisible(true);
        setLoading(true);
        setIllegalMemberIds([]);
        return post<OpenPlatformManagerResponse>(getRequestUrl(API.open.get_manager))
            .then((res) => {
                const nextMembers = res.data.members ?? [];
                setSavedMembers(nextMembers);
                setMembers(nextMembers);
            })
            .catch(() => {
                setVisible(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const save = () => {
        tracker(TRACKER.OPEN_PLATFORM_ADMIN_CONFIRM, {
            p1: 'confirm',
            p2: members.length
        });
        const memberIds = getMemberIds(members);
        if (isEqual(memberIds, getMemberIds(savedMembers))) {
            closeDialog();
            return;
        }

        return post<OpenPlatformManagerResponse>(getRequestUrl(API.open.update_manager), {
            member_ids: memberIds
        })
            .then((res) => {
                const nextMembers = res.data.members ?? [];
                setSavedMembers(nextMembers);
                setMembers(nextMembers);
                setIllegalMemberIds([]);
                setVisible(false);
                Message.add({
                    type: 'success',
                    messageType: 'toast',
                    message: platformIntl.get('fxp.base.open.open_platform_manager.save.success')
                });
            })
            .catch((error) => {
                const data = get(error, 'response.data');
                tracker(TRACKER.OPEN_PLATFORM_ADMIN_CONFIRM, {
                    p1: 'confirm',
                    p2: members.length,
                    p3: data?.code
                });
                if (data?.code === ECode.MANAGE_GROUP_ADD_OWNER) {
                    setIllegalMemberIds([data.meta.memberId]);
                }
            });
    };

    const cancel = () => {
        tracker(TRACKER.OPEN_PLATFORM_ADMIN_CONFIRM, {
            p1: 'cancel',
            p2: members.length
        });
        closeDialog();
    };

    return (
        <>
            <Button
                className="fx-open-platform-manager-entry"
                type="text-normal"
                icon={<MonoBaseOpenPlatformAdminIcon />}
                loadingWhenClick
                onClick={openDialog}
            >
                {platformIntl.get('fxp.base.open.open_admin.btn')}
            </Button>
            <Dialog
                className="fx-open-platform-manager-dialog"
                visible={visible}
                width={480}
                height={280}
                title={platformIntl.get('fxp.base.open.open_admin.open_admin_config.ttl')}
                text4Ok={platformIntl.get('save')}
                text4Cancel={platformIntl.get('cancel')}
                props4Ok={{ disabled: loading }}
                onOk={save}
                onCancel={cancel}
            >
                {loading ? (
                    <Spin />
                ) : (
                    <>
                        <div className="open-platform-manager-config">
                            <span className="open-platform-manager-config-title">
                                {platformIntl.get('fxp.base.bg.admin.admin.nm')}
                            </span>
                            <span className="open-platform-manager-tip">
                                {platformIntl.get('fxp.base.open.open_admin.open_admin_desc.tip')}
                            </span>
                        </div>
                        <ContactValuePanel
                            className="open-platform-manager-members"
                            dataType={DataType.member}
                            items={members}
                            errorIds={illegalMemberIds}
                            onClick={() => {
                                tracker(TRACKER.OPEN_PLATFORM_ADMIN_SELECT_MEMBER);
                                setMemberSelectVisible(true);
                            }}
                        />
                    </>
                )}
            </Dialog>
            <MemberSelectDialog
                visible={memberSelectVisible}
                value={members}
                errorIds={illegalMemberIds}
                onFetchInternalDepartments={() => getContactDeptList(authModel.tenantId)}
                onFetchInternalMembers={(params) =>
                    getContactMemberList(authModel.tenantId, {
                        ...params,
                        hasCount: true
                    })
                }
                onOk={(nextMembers = []) => {
                    tracker(TRACKER.OPEN_PLATFORM_ADMIN_SELECT_MEMBER_DIALOG, {
                        p1: 'confirm',
                        p2: nextMembers.length
                    });
                    setMembers(nextMembers);
                    setIllegalMemberIds([]);
                    setMemberSelectVisible(false);
                }}
                onCancel={() => {
                    tracker(TRACKER.OPEN_PLATFORM_ADMIN_SELECT_MEMBER_DIALOG, {
                        p1: 'cancel'
                    });
                    setMemberSelectVisible(false);
                }}
            />
        </>
    );
};

export default OpenPlatformManager;
